import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React, { useState } from 'react';

import { Navigate, useNavigate } from 'react-router-dom';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { useAuth } from 'context/AuthContext';
import { useGoogleAuth } from 'hooks/auth/useGoogleAuth';
import { useLogin } from 'hooks/auth/useLogin';
import { UserRole } from 'shared/constants';
import googleIcon from '../../assets/images/google-icon.svg';
import pawPrint from '../../assets/images/paw-print.svg';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [typeUser, setTypeUser] = useState<UserRole.CLIENT | UserRole.SITTER>(UserRole.CLIENT);

  const { mutate: login, isPending, error } = useLogin();
  const { handleGoogleLogin } = useGoogleAuth();
  const { user } = useAuth();

  if (user) {
    return (
      <Navigate
        to={user.activeRole === UserRole.SITTER ? '/sitter-dashboard' : '/dashboard'}
        replace
      />
    );
  }

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    login({
      email,
      password,
      activeRole: typeUser,
    });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
      }}
    >
      {/* Left Side - Login Form */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#FAF8F5',
          p: 4,
        }}
      >
        <ArrowBackIcon
          onClick={() => navigate('/')}
          sx={{
            position: 'absolute',
            top: 20,
            left: 20,
            cursor: 'pointer',
            color: 'black',
            fontSize: 30,
          }}
        />
        <Container maxWidth="xs">
          <Stack direction="row" spacing={4} justifyContent="center" mb={4}>
            <Box
              onClick={() => setTypeUser(UserRole.CLIENT)}
              sx={{
                border: '2px solid #178B8E',
                borderRadius: '8px',
                padding: '8px',
                width: '100%',
                maxWidth: '200px',
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: typeUser === UserRole.CLIENT ? '#178B8E' : 'transparent',
              }}
            >
              <Typography
                sx={{
                  color: typeUser === UserRole.CLIENT ? '#FFFFFF' : 'inherit',
                }}
              >
                As a Client
              </Typography>
            </Box>
            <Box
              onClick={() => setTypeUser(UserRole.SITTER)}
              sx={{
                cursor: 'pointer',
                border: '2px solid #178B8E',
                borderRadius: '8px',
                padding: '8px',
                width: '100%',
                maxWidth: '200px',
                textAlign: 'center',
                backgroundColor: typeUser === UserRole.SITTER ? '#178B8E' : 'transparent',
              }}
            >
              <Typography
                sx={{
                  color: typeUser === UserRole.SITTER ? '#FFFFFF' : 'inherit',
                }}
              >
                As a Sitter
              </Typography>
            </Box>
          </Stack>
          <Typography
            variant="h3"
            sx={{
              fontSize: '2.5rem',
              fontWeight: 700,
              color: '#1A1A1A',
              mb: 4,
              textAlign: 'center',
            }}
          >
            Log In
          </Typography>

          {/* Social Login Buttons */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 2,
              mb: 3,
            }}
          >
            {/* <IconButton
              onClick={() => console.log('Facebook login coming soon')}
              sx={{
                width: 55,
                height: 55,
                padding: 1.5,
                backgroundColor: '#3b5998',
                '&:hover': {
                  transform: 'scale(1.05)',
                  transition: 'transform 0.2s ease',
                  background: 'blue',
                },
              }}
            >
              <img src={facebookIcon} alt="Facebook" style={{ width: '100%', height: '100%' }} />
            </IconButton> */}
            <IconButton
              onClick={() => handleGoogleLogin(typeUser)}
              sx={{
                width: 55,
                height: 55,
                padding: 1.5,
                backgroundColor: '#fff',
                '&:hover': {
                  transform: 'scale(1.05)',
                  transition: 'transform 0.2s ease',
                },
              }}
            >
              <img src={googleIcon} alt="Google" style={{ width: '100%', height: '100%' }} />
            </IconButton>
            {/* <IconButton
              onClick={() => console.log('Instagram login coming soon')}
              sx={{
                width: 55,
                height: 55,
                padding: 1.5,
                background:
                  'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
                '&:hover': {
                  transform: 'scale(1.05)',
                  transition: 'transform 0.2s ease',
                  background:
                    'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
                },
              }}
            >
              <img src={instagramIcon} alt="Instagram" style={{ width: '100%', height: '100%' }} />
            </IconButton> */}
          </Box>

          {/* Divider */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Divider sx={{ flex: 1 }} />
            <Typography sx={{ px: 2, color: '#5A5A5A' }}>Or</Typography>
            <Divider sx={{ flex: 1 }} />
          </Box>

          {/* Login Form */}
          <Box component="form" onSubmit={handleLogin}>
            <TextField
              fullWidth
              placeholder="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#FFFFFF',
                },
              }}
            />
            <TextField
              fullWidth
              placeholder="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{
                mb: 1,
                '& .MuiOutlinedInput-root': { backgroundColor: '#FFFFFF' },
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
              <Link
                href="#"
                underline="none"
                sx={{
                  color: '#1A1A1A',
                  fontSize: '0.9rem',
                  '&:hover': { color: '#178B8E' },
                }}
              >
                Forgot Password?
              </Link>
            </Box>
            <Button
              type="submit"
              disabled={isPending}
              fullWidth
              variant="contained"
              size="large"
              sx={{
                backgroundColor: '#178B8E',
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: '#0F6164',
                },
              }}
            >
              {isPending ? 'Logging in...' : 'Log In'}
            </Button>
            {error && (
              <Typography color="error" sx={{ mt: 2, textAlign: 'center' }}>
                {(error as any)?.response?.data?.message || 'Login failed'}
              </Typography>
            )}
          </Box>

          {/* Mobile-only Sign Up link */}
          <Box
            sx={{
              display: { xs: 'flex', md: 'none' },
              justifyContent: 'center',
              alignItems: 'center',
              gap: 0.5,
              mt: 3,
            }}
          >
            <Typography variant="body2" sx={{ color: '#5A5A5A' }}>
              Don't have an account?
            </Typography>
            <Link
              onClick={() => navigate('/register')}
              underline="none"
              sx={{
                color: '#178B8E',
                fontWeight: 600,
                fontSize: '0.875rem',
                cursor: 'pointer',
                '&:hover': { color: '#0F6164' },
              }}
            >
              Sign Up
            </Link>
          </Box>
        </Container>
      </Box>

      {/* Right Side - Sign Up Prompt with Paw Prints */}
      <Box
        sx={{
          flex: 1,
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(199.64deg, #047979 0.24%, #50d9d7 105.21%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Content */}
        <Container
          maxWidth="sm"
          sx={{
            textAlign: 'center',
            zIndex: 100,
            position: 'relative',
          }}
        >
          <Typography
            variant="h2"
            sx={{
              fontSize: '3rem',
              fontWeight: 700,
              color: '#FFFFFF',
              mb: 3,
            }}
          >
            New Here?
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: '1.2rem',
              color: '#FFFFFF',
              mb: 4,
              lineHeight: 1.6,
              maxWidth: 300,
              margin: '0 auto 2rem',
            }}
          >
            Sign Up and discover our dedicated pet sitters!
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/register')}
            sx={{
              backgroundColor: '#FFFFFF',
              color: '#178B8E',
              py: 1.5,
              px: 5,
              fontSize: '1.1rem',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#F5F5F5',
              },
            }}
          >
            Sign Up
          </Button>
        </Container>

        {/* Paw Container */}
        <Box
          sx={{
            position: 'absolute',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 20,
            marginRight: '-250px',
            gap: 2,
          }}
        >
          {Array(7)
            .fill(null)
            .map((_, index) => {
              const rotations = [40, -10, 40, -10, 40, -10, 40];
              const offsets = [30, -250, 30, -250, 30, -250, 30];

              return (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    position: 'relative',
                    marginLeft: `${offsets[index]}px`,
                  }}
                >
                  <Box
                    component="img"
                    src={pawPrint}
                    alt=""
                    sx={{
                      width: 150,
                      height: 167,
                      opacity: 0.3,
                      filter: 'brightness(0) invert(1)',
                      transform: `rotate(${rotations[index]}deg)`,
                    }}
                  />
                </Box>
              );
            })}
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
